import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import { TransactionsTable } from '@/components/payments/transactions/TransactionsTable';
import { useGenerateReceipt } from '@/hooks/payments/receipts/useGenerateReceipt';
import { usePaymentTransactions } from '@/hooks/payments/transactions/usePaymentTransactions';

/** The dashboard preview shows the three most recent payments; the full history lives on its own tab. */
const RECENT_LIMIT = 3;

interface RecentTransactionsSectionProps {
  /** Switches the Payments page to its Past Transactions tab. */
  onViewAll: () => void;
  className?: string;
}

/**
 * Recent Transactions on the Payment Center dashboard: the same table the Past Transactions page
 * uses, in its compact mode. Owns its own query, like the other dashboard sections.
 *
 * Errors here are deliberately quiet. This is a summary alongside other cards, not the member's
 * primary view of their history, so a failure reads as a line of muted text rather than the page's
 * destructive alert with a retry.
 */
export function RecentTransactionsSection({ onViewAll, className }: RecentTransactionsSectionProps) {
  const { t } = useTranslation('members');
  const { data, isPending, isError } = usePaymentTransactions({ offset: 0, limit: RECENT_LIMIT });
  // Kept whole rather than destructured: its `isPending` would collide with the query's above.
  const receipt = useGenerateReceipt();

  const state = isError ? 'error' : isPending ? 'loading' : 'loaded';

  // Withheld only once the query has confirmed there is nothing to see, so the link does not appear
  // a moment after the rows do.
  const isKnownEmpty = data?.totalCount === 0;

  return (
    <section className={cn('mt-8 border-t pt-8', className)} data-slot="recent-transactions">
      <TransactionsTable
        transactions={data?.edges ?? []}
        state={state}
        limit={RECENT_LIMIT}
        title={t('payments.recentTransactions.sectionTitle')}
        description={t('payments.transactions.disclaimer', { phone: MDVIP_COMPANY_INFO.memberSupportPhone })}
        emptyMessage={t('payments.recentTransactions.emptyState')}
        onGenerateReceipt={receipt.onGenerateReceipt}
        isReceiptPending={receipt.isPending}
        onViewAll={isKnownEmpty ? undefined : onViewAll}
        viewAllLabel={isKnownEmpty ? undefined : t('payments.recentTransactions.viewAll')}
        errorContent={<p className="text-sm text-muted-foreground">{t('payments.recentTransactions.errorState')}</p>}
      />
    </section>
  );
}
